import { Component, ChangeDetectorRef, DoCheck, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { DirectionService } from 'src/app/direction.service';
import { ExhibitionComment, WallApiService, isWallCommentAuthorNameTruncated, kickWallLazyImagesNearView, nextWallProfileImage, scrollWallFocusElIntoView, scrollWallMediaToVisibleTop, wallCommentAuthorDisplayName, wallCommentTimeLabel } from '../wall-api.service';
import {
  WALL_VISIBLE_COMMENTS_LIMIT,
  VisibleCommentThread,
  allCommentThreads,
  sliceVisibleCommentThreads,
  wallCommentsLoadReservePx,
} from '../wall-comments-visible';

export interface WallSocialCard {
  _id: string;
  likeCount: number;
  commentCount: number;
  commenterCount?: number;
  likedByMe: boolean;
  recentLikers?: string[];
  recentCommenters?: string[];
  commentsOpen: boolean;
  commentsMounted: boolean;
  commentsLoading: boolean;
  comments: ExhibitionComment[];
  draftComment: string;
  liking: boolean;
  commenting: boolean;
  likers?: string[];
  likersExpanded?: boolean;
}

@Component({
  selector: 'app-wall-card-social',
  templateUrl: './wall-card-social.component.html',
  styleUrls: ['./wall-card-social.component.css'],
})
export class WallCardSocialComponent implements OnInit, OnChanges, DoCheck, OnDestroy {
  @Input() card: WallSocialCard;
  @Input() siblingCards: WallSocialCard[] = [];
  /** Hide new-comment compose (e.g. while editing work title/description on personal wall). */
  @Input() hideCommentCompose = false;
  /**
   * Focus panel without likes/comments body (e.g. personal-wall edit mode).
   * Owner projection and layout still work via focusLayout.
   */
  @Input() suppressComments = false;
  /** Fullscreen focus dialog layout: like under image, all comments, no overlays. */
  @Input() focusLayout = false;
  /** When true, do not truncate the comments list to the visible limit. */
  @Input() showAllComments = false;
  /** Parent owns open/close (e.g. personal wall focus overlay). */
  @Input() manageOpenExternally = false;
  /** Compact overlay for very narrow gallery cards (no comments control). */
  @Input() compactOverlay = false;
  /** When false, hide like count / likers tip on the overlay (e.g. slots ≤55px). */
  @Input() showLikeCount = true;
  @Output() openRequested = new EventEmitter<Event | undefined>();
  @Output() commentsLayoutChange = new EventEmitter<void>();

  readonly visibleCommentsLimit = WALL_VISIBLE_COMMENTS_LIMIT;

  @HostBinding('class.eazix-exhibition-card-open')
  get hostOpen(): boolean {
    return !!this.card?.commentsOpen;
  }

  @HostBinding('class.wall-card-social-focus')
  get hostFocus(): boolean {
    return this.focusLayout;
  }

  isRTL = true;
  isAuth = false;

  private directionSub: Subscription;
  private authSub: Subscription;
  private userSub: Subscription;
  private likeSubs: { [id: string]: Subscription } = {};
  private commentLikeSubs: { [id: string]: Subscription } = {};
  private commentSubs: { [id: string]: Subscription } = {};
  private replySubs: { [id: string]: Subscription } = {};
  private deleteCommentSubs: { [id: string]: Subscription } = {};
  private editCommentSubs: { [id: string]: Subscription } = {};
  private loadCommentsSubs: { [id: string]: Subscription } = {};
  private loadLikersSubs: { [id: string]: Subscription } = {};
  private loadCommentLikersSubs: { [id: string]: Subscription } = {};
  private prefetchCommenterSubs: { [id: string]: Subscription } = {};
  private commentsCloseTimers: { [id: string]: ReturnType<typeof setTimeout> } = {};
  private readonly commentsAnimMs = 380;
  private wasCommentsMounted = false;
  private wasCommentsOpen = false;
  private readonly onReplyOutsidePointerDown = (event: PointerEvent): void => {
    this.handleReplyOutsidePointerDown(event);
  };

  constructor(
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
    private directionService: DirectionService,
    private wallApi: WallApiService,
    private authService: AuthService,
    private router: Router,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.directionSub = this.directionService.direction$.subscribe((direction) => {
      this.isRTL = direction === 'rtl';
    });
    this.isAuth = this.authService.getIsAuth();
    this.authSub = this.authService
      .getAuthStatusListener()
      .subscribe((isAuth) => {
        this.isAuth = isAuth;
      });
    this.seedWallLikerNameFromUser(this.authService.getUser());
    this.userSub = this.authService.userUpdated$.subscribe((user) => {
      this.seedWallLikerNameFromUser(user);
    });
    if (this.focusLayout && this.card) {
      this.openCommentsPanel(this.card);
      this.composeHadText = !!(this.card.draftComment || '').trim();
      this.bindFocusVisualViewport();
    }
    document.addEventListener('pointerdown', this.onReplyOutsidePointerDown, true);
  }

  visibleComments(): VisibleCommentThread[] {
    const roots = this.card?.comments || [];
    if (this.showAllComments || this.focusLayout || this.card?.commentsMounted) {
      return allCommentThreads(roots);
    }
    return sliceVisibleCommentThreads(roots, this.visibleCommentsLimit);
  }

  commentsLoadReservePx(): number {
    const card = this.card;
    return wallCommentsLoadReservePx(
      card?.commentCount,
      Array.isArray(card?.comments) ? card.comments.length : 0
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const suppress = changes['suppressComments'];
    if (suppress && suppress.currentValue && !suppress.firstChange) {
      this.resetCommentInputs();
    }
  }

  ngDoCheck(): void {
    const card = this.card;
    const mounted = !!card?.commentsMounted;
    if (mounted && !this.wasCommentsMounted) {
      if (!this.suppressComments) {
        this.loadItemLikers(card);
        if (!card.comments.length) {
          this.loadComments(card);
        }
      }
    }
    this.wasCommentsMounted = mounted;
    const open = !!card?.commentsOpen;
    if (this.wasCommentsOpen && !open) {
      this.resetCommentInputs();
    }
    this.wasCommentsOpen = open;
  }

  ngOnDestroy(): void {
    this.directionSub?.unsubscribe();
    this.authSub?.unsubscribe();
    this.userSub?.unsubscribe();
    Object.values(this.likeSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.commentLikeSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.commentSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.replySubs).forEach((s) => s?.unsubscribe());
    Object.values(this.deleteCommentSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.editCommentSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.loadCommentsSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.loadLikersSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.loadCommentLikersSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.prefetchCommenterSubs).forEach((s) => s?.unsubscribe());
    Object.values(this.commentsCloseTimers).forEach((t) => clearTimeout(t));
    this.unbindFocusVisualViewport();
    if (this.composeScrollRaf != null) {
      cancelAnimationFrame(this.composeScrollRaf);
      this.composeScrollRaf = null;
    }
    document.removeEventListener('pointerdown', this.onReplyOutsidePointerDown, true);
  }

  trackCommentById(_index: number, c: ExhibitionComment): string {
    return c._id;
  }

  trackCommentThread(_index: number, thread: VisibleCommentThread): string {
    return thread?.comment?._id || String(_index);
  }

  commentInitial(comment: ExhibitionComment): string {
    const name = String(comment?.authorName || '').trim();
    return (name.charAt(0) || '?').toUpperCase();
  }

  shortAuthorName(name?: string): string {
    const n = String(name || '').trim();
    if (n.length <= 15) {
      return n;
    }
    return `${n.slice(0, 15)}...`;
  }

  /** Likers / comment authors: truncate above limit (longer in focus dialog). */
  likerDisplayName(name?: string): string {
    const n = String(name || '').trim();
    const limit = this.userNameTruncateLimit();
    if (n.length <= limit) {
      return n;
    }
    return `${n.slice(0, limit)}...`;
  }

  commentAuthorDisplayName(name?: string): string {
    return wallCommentAuthorDisplayName(name);
  }

  isCommentAuthorNameTruncated(name?: string): boolean {
    return isWallCommentAuthorNameTruncated(name);
  }

  /** Shorten long comments; "more" expands to full text. */
  showCommentTextMore(comment: ExhibitionComment): boolean {
    return (
      !!comment?._id &&
      !this.isCommentTextExpanded(comment) &&
      this.overflowCommentTextIds.has(comment._id)
    );
  }

  showCommentTextLess(comment: ExhibitionComment): boolean {
    return (
      !!comment?._id &&
      this.isCommentTextExpanded(comment) &&
      this.overflowCommentTextIds.has(comment._id)
    );
  }

  onCommentTextOverflow(commentId: string, overflowing: boolean): void {
    if (!commentId) {
      return;
    }
    if (overflowing) {
      if (!this.overflowCommentTextIds.has(commentId)) {
        this.overflowCommentTextIds.add(commentId);
        this.commentsLayoutChange.emit();
      }
      return;
    }
    if (!this.expandedCommentTextIds.has(commentId)) {
      this.overflowCommentTextIds.delete(commentId);
    }
    this.commentsLayoutChange.emit();
    this.cdr.markForCheck();
  }

  expandCommentText(comment: ExhibitionComment, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (comment?._id) {
      this.expandedCommentTextIds.add(comment._id);
    }
  }

  collapseCommentText(comment: ExhibitionComment, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (comment?._id) {
      this.expandedCommentTextIds.delete(comment._id);
    }
  }

  isCommentTextExpanded(comment: ExhibitionComment): boolean {
    return !!comment?._id && this.expandedCommentTextIds.has(comment._id);
  }

  private forgetCommentTextLayout(commentId?: string): void {
    if (!commentId) {
      return;
    }
    this.expandedCommentTextIds.delete(commentId);
    this.overflowCommentTextIds.delete(commentId);
  }

  private notifyCommentsLayout(): void {
    this.cdr.detectChanges();
    this.commentsLayoutChange.emit();
  }

  private readonly expandedCommentTextIds = new Set<string>();
  private readonly overflowCommentTextIds = new Set<string>();
  private readonly wallLikerNameKey = 'wallLikerDisplayName';
  /** Tracks whether compose had non-empty text (for send-btn reveal scroll). */
  private composeHadText = false;
  private composeScrollRaf: number | null = null;
  private visualViewportHandler: (() => void) | null = null;

  isLikerNameTruncated(name?: string): boolean {
    return String(name || '').trim().length > this.userNameTruncateLimit();
  }

  /** Focus dialog: ~50% longer names before truncation (8 → 12). */
  private userNameTruncateLimit(): number {
    return this.focusLayout ? 12 : 8;
  }

  /** Click shows full truncated name (desktop + mobile). Leave hover to close. */
  showTruncatedNameTip(event: Event, tip: MatTooltip, name?: string): void {
    if (!String(name || '').trim()) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    tip.show(0);
  }

  hideTruncatedNameTip(tip: MatTooltip): void {
    tip?.hide(0);
  }

  commentTimeLabel(comment: ExhibitionComment): string {
    return wallCommentTimeLabel(comment?.created);
  }

  onCommentAvatarError(comment: ExhibitionComment): void {
    comment.authorImageURL = nextWallProfileImage(
      comment.authorImageURL,
      comment.authorImageCandidates
    );
  }

  openFromImage(event?: Event): void {
    event?.stopPropagation();
    if (this.manageOpenExternally) {
      this.openRequested.emit(event);
      return;
    }
    this.toggleComments(event);
  }

  onLike(event?: Event): void {
    const card = this.card;
    event?.stopPropagation();
    if (!this.isAuth) {
      this.router.navigate(['/auth/login']);
      return;
    }
    if (!card || card.liking) {
      return;
    }
    card.liking = true;
    const prevLiked = card.likedByMe;
    const prevCount = card.likeCount;
    const prevLikers = [...(card.recentLikers || [])];
    const prevAllLikers = [...(card.likers || [])];
    this.applyLocalLike(card, !prevLiked);
    this.syncCardLikers(card, !prevLiked);

    this.likeSubs[card._id]?.unsubscribe();
    this.likeSubs[card._id] = this.wallApi.toggleItemLike(card._id).subscribe({
        next: (res) => {
          card.likedByMe = res.liked === true;
          card.likeCount = Number(res.likeCount) || 0;
          if (Array.isArray(res.recentLikers)) {
            card.recentLikers = res.recentLikers;
          }
          this.reconcileCardLikersAfterToggle(card, res.liked === true, res.recentLikers);
          if (card.commentsMounted) {
            this.loadItemLikers(card);
          }
          if (card.likeCount <= this.likersPreviewLimit()) {
            card.likersExpanded = false;
          }
          card.liking = false;
        },
      error: () => {
        card.likedByMe = prevLiked;
        card.likeCount = prevCount;
        card.recentLikers = prevLikers;
        card.likers = prevAllLikers;
        card.liking = false;
        this.showSnack('wall.exhibition.like-error');
      },
    });
  }

  onCommentLike(comment: ExhibitionComment, event?: Event): void {
    const card = this.card;
    event?.stopPropagation();
    if (!this.isAuth) {
      this.router.navigate(['/auth/login']);
      return;
    }
    if (!card || comment.liking) {
      return;
    }
    comment.liking = true;
    const prevLiked = comment.likedByMe === true;
    const prevCount = Number(comment.likeCount) || 0;
    const prevLikers = [...(comment.recentLikers || [])];
    const prevAllLikers = [...(comment.likers || [])];
    this.applyLocalLike(comment, !prevLiked);
    this.syncCommentLikers(comment, !prevLiked);

    this.commentLikeSubs[comment._id]?.unsubscribe();
    this.commentLikeSubs[comment._id] = this.wallApi
      .toggleCommentLike(card._id, comment._id)
      .subscribe({
        next: (res) => {
          comment.likedByMe = res.liked === true;
          comment.likeCount = Number(res.likeCount) || 0;
          if (Array.isArray(res.recentLikers)) {
            comment.recentLikers = res.recentLikers;
          }
          this.reconcileCommentLikersAfterToggle(
            comment,
            res.liked === true,
            res.recentLikers
          );
          if (comment.likeCount <= 0) {
            comment.likersExpanded = false;
          } else if (comment.likersExpanded) {
            this.loadCommentLikers(comment);
          }
          comment.liking = false;
        },
        error: () => {
          comment.likedByMe = prevLiked;
          comment.likeCount = prevCount;
          comment.recentLikers = prevLikers;
          comment.likers = prevAllLikers;
          comment.liking = false;
          this.showSnack('wall.exhibition.like-error');
        },
      });
  }

  topLikers(names?: string[]): string[] {
    return (names || []).filter((n) => !!String(n || '').trim()).slice(0, 3);
  }

  hasMoreCommenters(): boolean {
    return (Number(this.card?.commenterCount) || 0) > 3;
  }

  visibleLikers(): string[] {
    const card = this.card;
    const names = (card.likers?.length ? card.likers : card.recentLikers || [])
      .map((n) => String(n || '').trim())
      .filter(Boolean);
    if (card.likersExpanded) {
      return names;
    }
    return names.slice(0, this.likersPreviewLimit());
  }

  canExpandLikers(): boolean {
    return (Number(this.card?.likeCount) || 0) > this.likersPreviewLimit();
  }

  /** Focus dialog: 6 mobile / 12 desktop; card panel: 3. */
  private likersPreviewLimit(): number {
    if (!this.focusLayout) {
      return 3;
    }
    return typeof window !== 'undefined' && window.innerWidth <= 640 ? 6 : 12;
  }

  toggleLikersExpand(event?: Event): void {
    event?.stopPropagation();
    const card = this.card;
    card.likersExpanded = !card.likersExpanded;
    if (card.likersExpanded) {
      this.loadItemLikers(card);
    }
  }

  commentLikers(comment: ExhibitionComment): string[] {
    return (comment.likers?.length ? comment.likers : comment.recentLikers || [])
      .map((n) => String(n || '').trim())
      .filter(Boolean);
  }

  toggleCommentLikers(comment: ExhibitionComment, event?: Event): void {
    event?.stopPropagation();
    comment.likersExpanded = !comment.likersExpanded;
    if (comment.likersExpanded) {
      this.loadCommentLikers(comment);
    }
  }

  ensureCommentersTip(): void {
    const card = this.card;
    if ((Number(card.commentCount) || 0) <= 0) {
      return;
    }
    if (this.topLikers(card.recentCommenters).length) {
      return;
    }
    if (card.comments?.length) {
      this.refreshRecentCommenters(card);
      return;
    }
    if (card.commentsLoading) {
      return;
    }
    this.prefetchCommenterSubs[card._id]?.unsubscribe();
    this.prefetchCommenterSubs[card._id] = this.wallApi
      .getItemComments(card._id)
      .subscribe({
        next: (res) => {
          const incoming = Array.isArray(res?.comments) ? res.comments : [];
          if (!card.comments.length) {
            card.comments = this.nestComments(incoming);
            card.commentCount = this.countComments(card);
          }
          this.refreshRecentCommenters(card);
        },
        error: () => {},
      });
  }

  toggleComments(event?: Event): void {
    const card = this.card;
    event?.stopPropagation();
    if (this.manageOpenExternally) {
      this.openRequested.emit(event);
      return;
    }
    if (card.commentsOpen) {
      this.closeCommentsPanel(card);
      return;
    }
    (this.siblingCards || []).forEach((c) => {
      if (c._id !== card._id && (c.commentsOpen || c.commentsMounted)) {
        this.closeCommentsPanel(c);
      }
    });
    this.openCommentsPanel(card);
  }

  private openCommentsPanel(card: WallSocialCard): void {
    clearTimeout(this.commentsCloseTimers[card._id]);
    delete this.commentsCloseTimers[card._id];
    if (!card.comments.length) {
      this.loadComments(card);
    }
    card.commentsMounted = true;
    this.loadItemLikers(card);
    setTimeout(() => {
      card.commentsOpen = true;
      if (!this.focusLayout) {
        this.scrollOpenedCardIntoView();
      }
      if (card.comments.length) {
        this.scrollCommentsToTop(card._id);
      }
    }, 0);
  }

  private closeCommentsPanel(card: WallSocialCard): void {
    this.resetCommentInputs();
    card.commentsOpen = false;
    clearTimeout(this.commentsCloseTimers[card._id]);
    this.commentsCloseTimers[card._id] = setTimeout(() => {
      if (!card.commentsOpen) {
        card.commentsMounted = false;
      }
      delete this.commentsCloseTimers[card._id];
    }, this.commentsAnimMs);
  }

  loadComments(card: WallSocialCard): void {
    if (card.comments.length) {
      return;
    }
    const inflight = this.loadCommentsSubs[card._id];
    if (card.commentsLoading && inflight && !inflight.closed) {
      return;
    }
    card.commentsLoading = true;
    this.prefetchCommenterSubs[card._id]?.unsubscribe();
    this.loadCommentsSubs[card._id]?.unsubscribe();
    this.loadCommentsSubs[card._id] = this.wallApi
      .getItemComments(card._id)
      .subscribe({
        next: (res) => {
          const incoming = Array.isArray(res?.comments) ? res.comments : [];
          card.comments = this.nestComments(incoming);
          card.commentCount = this.countComments(card);
          this.refreshRecentCommenters(card);
          card.commentsLoading = false;
          this.cdr.detectChanges();
          this.commentsLayoutChange.emit();
          setTimeout(() => this.scrollCommentsToTop(card._id), 0);
        },
        error: () => {
          card.commentsLoading = false;
          this.showSnack('wall.exhibition.comments-error');
          this.cdr.detectChanges();
          this.commentsLayoutChange.emit();
        },
      });
  }

  submitComment(): void {
    const card = this.card;
    if (!this.isAuth) {
      this.router.navigate(['/auth/login']);
      return;
    }
    const text = String(card.draftComment || '').trim();
    if (!text || card.commenting) {
      return;
    }
    card.commenting = true;
    this.commentSubs[card._id]?.unsubscribe();
    this.commentSubs[card._id] = this.wallApi
      .addItemComment(card._id, text)
      .subscribe({
        next: (res) => {
          const newId = res?.comment?._id ? String(res.comment._id) : '';
          if (res?.comment) {
            card.comments = [this.hydrateComment(res.comment), ...card.comments];
          }
          card.commentCount =
            res?.commentCount != null
              ? res.commentCount
              : this.countComments(card);
          this.refreshRecentCommenters(card);
          card.draftComment = '';
          card.commenting = false;
          this.composeHadText = false;
          this.clearFocusScrollerComposeHacks();
          setTimeout(() => {
            this.resetComposeField(card._id);
            if (this.focusLayout && newId) {
              this.scrollNewCommentIntoView(newId);
            } else {
              this.scrollCommentsToTop(card._id);
            }
          }, 0);
        },
        error: () => {
          card.commenting = false;
          this.showSnack('wall.exhibition.comment-error');
        },
      });
  }

  deleteComment(comment: ExhibitionComment): void {
    const card = this.card;
    if (!this.isMyComment(comment) || comment.saving) {
      return;
    }
    this.deleteCommentSubs[comment._id]?.unsubscribe();
    this.deleteCommentSubs[comment._id] = this.wallApi
      .deleteItemComment(card._id, comment._id)
      .subscribe({
        next: (res) => {
          this.removeCommentFromCard(card, comment._id);
          this.forgetCommentTextLayout(comment._id);
          card.commentCount =
            res?.commentCount != null
              ? res.commentCount
              : this.countComments(card);
          this.refreshRecentCommenters(card);
          this.notifyCommentsLayout();
        },
        error: () => {
          this.showSnack('wall.exhibition.comment-delete-error');
        },
      });
  }

  startEditComment(comment: ExhibitionComment, event?: Event): void {
    event?.stopPropagation();
    if (!this.isMyComment(comment) || comment.saving) {
      return;
    }
    this.flattenComments(this.card).forEach((c) => {
      if (c._id !== comment._id) {
        this.cancelEditComment(c);
      }
      c.confirmDelete = false;
    });
    comment.editing = true;
    comment.draftText = comment.text;
    setTimeout(() => {
      this.resizeCommentFieldById(comment._id);
      this.notifyCommentsLayout();
    }, 0);
  }

  cancelEditComment(comment: ExhibitionComment): void {
    if (comment.saving) {
      return;
    }
    comment.editing = false;
    comment.draftText = comment.text;
    comment.confirmDelete = false;
    this.notifyCommentsLayout();
  }

  isCommentDirty(comment: ExhibitionComment): boolean {
    return (
      String(comment.draftText || '').trim() !== String(comment.text || '').trim()
    );
  }

  resizeCommentField(event: Event): void {
    const el = event.target as HTMLTextAreaElement | null;
    if (el) {
      this.fitCommentTextarea(el);
    }
  }

  /** Main compose: grow field; keep send button visible in the focus dialog. */
  onComposeInput(event: Event): void {
    this.resizeCommentField(event);
    const card = this.card;
    const hasText = !!(card?.draftComment || '').trim();
    const sendToggled = hasText !== this.composeHadText;
    this.composeHadText = hasText;
    if (sendToggled && this.focusLayout) {
      this.notifyCommentsLayout();
    }
    if (!card || !this.focusLayout) {
      return;
    }
    if (!hasText) {
      this.clearFocusScrollerComposeHacks();
      return;
    }
    this.queueScrollComposeSendIntoView();
  }

  /** Reply compose: grow field; keep send-reply visible at the bottom while typing. */
  onReplyInput(event: Event, comment: ExhibitionComment): void {
    this.resizeCommentField(event);
    if (!comment?.replying || !comment?._id) {
      return;
    }
    this.cdr.detectChanges();
    this.queueScrollReplySendIntoView(comment._id);
  }

  private queueScrollReplySendIntoView(commentId: string): void {
    if (typeof window === 'undefined') {
      return;
    }
    const reveal = () => {
      const btn = this.host.nativeElement.querySelector(
        `[data-reply-send="${commentId}"]`,
      ) as HTMLElement | null;
      const compose = this.host.nativeElement.querySelector(
        `[data-reply-compose="${commentId}"]`,
      ) as HTMLElement | null;
      const target = btn || compose;
      if (target) {
        scrollWallFocusElIntoView(target, { pinEnd: true });
      }
    };
    this.cdr.detectChanges();
    window.requestAnimationFrame(() => {
      reveal();
      window.setTimeout(reveal, 50);
      window.setTimeout(reveal, 150);
    });
  }

  private clearFocusScrollerComposeHacks(): void {
    const legacy = this.host.nativeElement.closest(
      '.wall-item-focus-scroll'
    ) as HTMLElement | null;
    const social = this.host.nativeElement.querySelector(
      '.wall-card-social-scroll'
    ) as HTMLElement | null;
    [legacy, social].forEach((scroller) => {
      if (!scroller) {
        return;
      }
      scroller.style.maxHeight = '';
      scroller.style.paddingBottom = '';
      scroller.style.overflowY = '';
      delete scroller.dataset['focusPadBase'];
      delete scroller.dataset['focusMaxH'];
    });
    legacy?.dispatchEvent(
      new CustomEvent('wall-focus-ensure-scroll', { bubbles: true })
    );
  }

  private bindFocusVisualViewport(): void {
    if (typeof window === 'undefined' || !window.visualViewport) {
      return;
    }
    this.unbindFocusVisualViewport();
    this.visualViewportHandler = () => {
      this.queueScrollComposeSendIntoView();
      const open = this.findOpenReplyComment();
      if (open?._id) {
        this.queueScrollReplySendIntoView(open._id);
      }
    };
    window.visualViewport.addEventListener('resize', this.visualViewportHandler);
    window.visualViewport.addEventListener('scroll', this.visualViewportHandler);
  }

  private unbindFocusVisualViewport(): void {
    if (!this.visualViewportHandler || typeof window === 'undefined') {
      return;
    }
    window.visualViewport?.removeEventListener(
      'resize',
      this.visualViewportHandler
    );
    window.visualViewport?.removeEventListener(
      'scroll',
      this.visualViewportHandler
    );
    this.visualViewportHandler = null;
  }

  private queueScrollComposeSendIntoView(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (this.composeScrollRaf != null) {
      cancelAnimationFrame(this.composeScrollRaf);
    }
    this.composeScrollRaf = window.requestAnimationFrame(() => {
      this.composeScrollRaf = window.requestAnimationFrame(() => {
        this.composeScrollRaf = null;
        this.scrollComposeSendIntoViewIfNeeded();
      });
    });
  }

  private scrollComposeSendIntoViewIfNeeded(retries = 3): void {
    if (!this.focusLayout || !this.card) {
      return;
    }
    let btn: HTMLElement | null = null;
    if ((this.card.draftComment || '').trim()) {
      btn = this.host.nativeElement.querySelector(
        `[data-compose-send="${this.card._id}"]`
      ) as HTMLElement | null;
    }
    if (!btn) {
      const replying = (this.card.comments || []).find(
        (c) => c.replying && !!(c.draftReply || '').trim()
      );
      if (replying?._id) {
        btn = this.host.nativeElement.querySelector(
          `[data-reply-send="${replying._id}"]`
        ) as HTMLElement | null;
      }
    }
    if (btn) {
      const pinEnd = btn.hasAttribute('data-reply-send');
      scrollWallFocusElIntoView(btn, pinEnd ? { pinEnd: true } : undefined);
      return;
    }
    // Send button is created with *ngIf on first character — retry briefly.
    if (retries > 0 && typeof window !== 'undefined') {
      window.requestAnimationFrame(() =>
        this.scrollComposeSendIntoViewIfNeeded(retries - 1)
      );
    }
  }

  saveEditComment(comment: ExhibitionComment): void {
    const card = this.card;
    if (!this.isMyComment(comment) || comment.saving) {
      return;
    }
    const text = String(comment.draftText || '').trim();
    if (!text || text === comment.text) {
      this.cancelEditComment(comment);
      return;
    }
    comment.saving = true;
    this.editCommentSubs[comment._id]?.unsubscribe();
    this.editCommentSubs[comment._id] = this.wallApi
      .updateItemComment(card._id, comment._id, text)
      .subscribe({
        next: (res) => {
          comment.text = res?.comment?.text != null ? res.comment.text : text;
          comment.editing = false;
          comment.confirmDelete = false;
          comment.saving = false;
          this.notifyCommentsLayout();
        },
        error: () => {
          comment.saving = false;
          this.showSnack('wall.exhibition.comment-edit-error');
        },
      });
  }

  isMyComment(comment: ExhibitionComment): boolean {
    return (
      this.isAuth &&
      !!comment?.userID &&
      comment.userID === this.authService.getUserId()
    );
  }

  hasOpenReply(): boolean {
    return (this.card?.comments || []).some((c) => c.replying);
  }

  private findOpenReplyComment(): ExhibitionComment | null {
    return (this.card?.comments || []).find((c) => c.replying) || null;
  }

  closeOpenReply(): void {
    const open = this.findOpenReplyComment();
    if (!open) {
      return;
    }
    open.replying = false;
    this.commentsLayoutChange.emit();
    this.cdr.markForCheck();
  }

  private handleReplyOutsidePointerDown(event: PointerEvent): void {
    if (!this.hasOpenReply()) {
      return;
    }
    const node = event.target instanceof Node ? event.target : null;
    if (!node) {
      return;
    }
    const el = node instanceof Element ? node : node.parentElement;
    if (el?.closest('.cdk-overlay-container, .cdk-overlay-pane')) {
      return;
    }
    if (
      el?.closest(
        '.eazix-exhibition-reply-compose, [data-reply-send], .eazix-exhibition-reply-btn'
      )
    ) {
      return;
    }
    this.closeOpenReply();
  }

  toggleReply(parent: ExhibitionComment, event?: Event): void {
    event?.stopPropagation();
    if (parent.parentId) {
      return;
    }
    if (!this.isAuth) {
      this.router.navigate(['/auth/login']);
      return;
    }
    const next = !parent.replying;
    this.card.comments.forEach((c) => {
      if (c._id !== parent._id) {
        c.replying = false;
      }
    });
    parent.replying = next;
    if (next) {
      setTimeout(() => {
        this.focusReplyField(parent._id);
        this.queueScrollReplySendIntoView(parent._id);
      }, 0);
    } else {
      (event?.currentTarget as HTMLElement)?.blur?.();
    }
    this.commentsLayoutChange.emit();
  }

  submitReply(parent: ExhibitionComment): void {
    const card = this.card;
    if (!this.isAuth) {
      this.router.navigate(['/auth/login']);
      return;
    }
    const text = String(parent.draftReply || '').trim();
    if (!text || parent.commentingReply || parent.parentId) {
      return;
    }
    parent.commentingReply = true;
    this.replySubs[parent._id]?.unsubscribe();
    this.replySubs[parent._id] = this.wallApi
      .addItemComment(card._id, text, parent._id)
      .subscribe({
        next: (res) => {
          const reply = res?.comment ? this.hydrateComment(res.comment) : null;
          const newId = reply?._id ? String(reply._id) : '';
          if (reply) {
            parent.replies = [reply, ...(parent.replies || [])];
          }
          card.commentCount =
            res?.commentCount != null
              ? res.commentCount
              : this.countComments(card);
          this.refreshRecentCommenters(card);
          parent.draftReply = '';
          parent.commentingReply = false;
          parent.replying = false;
          if (this.focusLayout && newId) {
            setTimeout(() => this.scrollNewCommentIntoView(newId), 0);
          }
        },
        error: () => {
          parent.commentingReply = false;
          this.showSnack('wall.exhibition.comment-error');
        },
      });
  }

  private loadItemLikers(card: WallSocialCard): void {
    this.loadLikersSubs[card._id]?.unsubscribe();
    this.loadLikersSubs[card._id] = this.wallApi.getItemLikes(card._id).subscribe({
      next: (res) => {
        card.likers = Array.isArray(res?.names) ? res.names : [];
        if (res?.likeCount != null) {
          card.likeCount = Number(res.likeCount) || 0;
        }
        this.commentsLayoutChange.emit();
        this.cdr.markForCheck();
      },
      error: () => {},
    });
  }

  private loadCommentLikers(comment: ExhibitionComment): void {
    const card = this.card;
    this.loadCommentLikersSubs[comment._id]?.unsubscribe();
    this.loadCommentLikersSubs[comment._id] = this.wallApi
      .getCommentLikes(card._id, comment._id)
      .subscribe({
        next: (res) => {
          comment.likers = Array.isArray(res?.names) ? res.names : [];
          if (res?.likeCount != null) {
            comment.likeCount = Number(res.likeCount) || 0;
          }
          if (comment.likeCount <= 0) {
            comment.likersExpanded = false;
          }
        },
        error: () => {},
      });
  }

  private syncCommentLikers(comment: ExhibitionComment, liked: boolean): void {
    const mine = this.myLikerName();
    if (!mine) {
      return;
    }
    const names = this.stripMyLikerNames([
      ...(comment.likers?.length ? comment.likers : comment.recentLikers || []),
    ]);
    comment.likers = liked ? [mine, ...names] : names;
  }

  private applyLocalLike(
    target: {
      likedByMe?: boolean;
      likeCount?: number;
      recentLikers?: string[];
    },
    liked: boolean
  ): void {
    const prevCount = Number(target.likeCount) || 0;
    target.likedByMe = liked;
    target.likeCount = Math.max(0, prevCount + (liked ? 1 : -1));
    const mine = this.myLikerName();
    if (!mine) {
      return;
    }
    const names = this.stripMyLikerNames([...(target.recentLikers || [])]);
    if (liked) {
      target.recentLikers = [mine, ...names].slice(0, 3);
    } else {
      target.recentLikers = names;
    }
  }

  private syncCardLikers(card: WallSocialCard, liked: boolean): void {
    const mine = this.myLikerName();
    if (!mine) {
      return;
    }
    const names = this.stripMyLikerNames([
      ...(card.likers?.length ? card.likers : card.recentLikers || []),
    ]);
    card.likers = liked ? [mine, ...names] : names;
  }

  /**
   * Auth userName is often email/username; wall likes use displayName.
   * Prefer cached/server display name so pills don't flash with "..." then shrink.
   */
  private myLikerName(): string {
    const cached = String(
      sessionStorage.getItem(this.wallLikerNameKey) || ''
    ).trim();
    if (cached) {
      return cached;
    }
    const user = this.authService.getUser();
    const display = String(user?.displayName || '').trim();
    if (display && !display.includes('@')) {
      return display;
    }
    const username = String(user?.username || '').trim();
    if (username && !username.includes('@')) {
      return username;
    }
    const stored = String(localStorage.getItem('userName') || '').trim();
    if (stored && !stored.includes('@')) {
      return stored;
    }
    return display || username || stored;
  }

  private myLikerNameAliases(): string[] {
    const aliases = new Set<string>();
    const add = (v?: string) => {
      const n = String(v || '').trim();
      if (n) {
        aliases.add(n);
      }
    };
    add(this.myLikerName());
    add(sessionStorage.getItem(this.wallLikerNameKey));
    add(localStorage.getItem('userName'));
    const user = this.authService.getUser();
    add(user?.displayName);
    add(user?.username);
    return [...aliases];
  }

  private stripMyLikerNames(names: string[]): string[] {
    const mine = new Set(this.myLikerNameAliases());
    return names.filter((n) => !mine.has(String(n || '').trim()));
  }

  private cacheWallLikerName(name?: string): void {
    const n = String(name || '').trim();
    if (!n || n.includes('@')) {
      return;
    }
    sessionStorage.setItem(this.wallLikerNameKey, n);
  }

  private seedWallLikerNameFromUser(user: any): void {
    if (!user) {
      return;
    }
    const display = String(user.displayName || '').trim();
    if (display && !display.includes('@')) {
      this.cacheWallLikerName(display);
      return;
    }
    const username = String(user.username || '').trim();
    if (username && !username.includes('@')) {
      this.cacheWallLikerName(username);
    }
  }

  private reconcileCardLikersAfterToggle(
    card: WallSocialCard,
    liked: boolean,
    recentLikers?: string[]
  ): void {
    const recent = Array.isArray(recentLikers) ? recentLikers : [];
    const serverMine = liked ? String(recent[0] || '').trim() : '';
    if (serverMine) {
      this.cacheWallLikerName(serverMine);
    }
    const names = this.stripMyLikerNames([
      ...(card.likers?.length ? card.likers : card.recentLikers || []),
    ]);
    card.likers = liked && serverMine ? [serverMine, ...names] : names;
  }

  private reconcileCommentLikersAfterToggle(
    comment: ExhibitionComment,
    liked: boolean,
    recentLikers?: string[]
  ): void {
    const recent = Array.isArray(recentLikers) ? recentLikers : [];
    const serverMine = liked ? String(recent[0] || '').trim() : '';
    if (serverMine) {
      this.cacheWallLikerName(serverMine);
    }
    const names = this.stripMyLikerNames([
      ...(comment.likers?.length ? comment.likers : comment.recentLikers || []),
    ]);
    comment.likers = liked && serverMine ? [serverMine, ...names] : names;
  }

  private resizeCommentFieldById(commentId: string): void {
    const el = document.querySelector(
      `[data-comment-edit="${commentId}"]`
    ) as HTMLTextAreaElement | null;
    if (el) {
      this.fitCommentTextarea(el);
      el.focus();
    }
  }

  private fitCommentTextarea(el: HTMLTextAreaElement): void {
    const maxPx = 100;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, maxPx)}px`;
    // In focus dialog, avoid scrollIntoView on open/layout — it nudges the dialog scroll.
    // Compose/send scrolling is handled explicitly in onComposeInput / scrollFocusElIntoView.
  }

  private resetComposeField(cardId: string): void {
    const el = document.querySelector(
      `[data-compose-edit="${cardId}"]`
    ) as HTMLTextAreaElement | null;
    if (el) {
      el.style.height = 'auto';
    }
  }

  private scrollCommentsToTop(cardId: string): void {
    const scroller = (this.focusLayout
      ? this.host.nativeElement.querySelector('.wall-card-social-scroll')
      : this.host.nativeElement.querySelector('.eazix-exhibition-comments-clip')) as HTMLElement | null;
    if (scroller) {
      scroller.scrollTop = 0;
    }
    const el = document.querySelector(
      `[data-comments-list="${cardId}"]`
    ) as HTMLElement | null;
    if (el) {
      el.scrollTop = 0;
    }
    if (this.focusLayout) {
      this.host.nativeElement.dispatchEvent(
        new CustomEvent('wall-focus-relayout', { bubbles: true })
      );
    }
  }

  /** Focus dialog: fast-scroll new comment into the visible scroll area if needed. */
  private scrollNewCommentIntoView(commentId: string): void {
    requestAnimationFrame(() => {
      const el = this.host.nativeElement.querySelector(
        `[data-comment-id="${commentId}"]`
      ) as HTMLElement | null;
      if (el) {
        scrollWallFocusElIntoView(el);
      }
    });
  }

  /** Scroll element into the visible dialog / comments / visual-viewport area. */
  private scrollFocusElIntoView(el: HTMLElement): void {
    scrollWallFocusElIntoView(el);
  }

  private scrollOpenedCardIntoView(): void {
    const media =
      (this.host.nativeElement.querySelector('[wallMedia]') as HTMLElement | null) ||
      (this.host.nativeElement.querySelector('.wall-card-social-media') as HTMLElement | null);
    scrollWallMediaToVisibleTop(media, () => {
      kickWallLazyImagesNearView(document);
      document.dispatchEvent(new CustomEvent('wall-feed-catch-up'));
    });
    setTimeout(() => {
      kickWallLazyImagesNearView(document);
      document.dispatchEvent(new CustomEvent('wall-feed-catch-up'));
    }, this.commentsAnimMs + 40);
  }

  private hydrateComment(raw: ExhibitionComment): ExhibitionComment {
    return {
      ...raw,
      parentId: raw.parentId || null,
      replies: Array.isArray(raw.replies)
        ? raw.replies.map((r) => this.hydrateComment(r))
        : [],
      editing: false,
      draftText: raw.text,
      saving: false,
      confirmDelete: false,
      replying: false,
      draftReply: '',
      commentingReply: false,
      likeCount: Number(raw.likeCount) || 0,
      likedByMe: raw.likedByMe === true,
      recentLikers: Array.isArray(raw.recentLikers) ? raw.recentLikers : [],
      likers: Array.isArray(raw.recentLikers) ? [...raw.recentLikers] : [],
      likersExpanded: false,
      liking: false,
    };
  }

  private nestComments(incoming: ExhibitionComment[]): ExhibitionComment[] {
    const byId = new Map<string, ExhibitionComment>();
    incoming.forEach((raw) => {
      const c = this.hydrateComment(raw);
      c.replies = [];
      byId.set(c._id, c);
    });
    const roots: ExhibitionComment[] = [];
    incoming.forEach((raw) => {
      const c = byId.get(raw._id);
      if (!c) {
        return;
      }
      const parentId = c.parentId ? String(c.parentId) : '';
      const parent = parentId ? byId.get(parentId) : null;
      if (parent && !parent.parentId) {
        parent.replies = parent.replies || [];
        parent.replies.push(c);
      } else {
        roots.push(c);
      }
    });
    return roots;
  }

  private flattenComments(card: WallSocialCard): ExhibitionComment[] {
    const out: ExhibitionComment[] = [];
    (card.comments || []).forEach((c) => {
      out.push(c);
      if (c.replies?.length) {
        out.push(...c.replies);
      }
    });
    return out;
  }

  /** Clear compose / reply / in-progress edit drafts (leave card or switch to edit). */
  resetCommentInputs(): void {
    const card = this.card;
    if (!card) {
      return;
    }
    card.draftComment = '';
    this.composeHadText = false;
    this.discardCardEdits(card);
    this.clearFocusScrollerComposeHacks();
    this.resetComposeField(card._id);
  }

  private discardCardEdits(card: WallSocialCard): void {
    this.flattenComments(card).forEach((c) => {
      if (!c.saving) {
        c.editing = false;
        c.draftText = c.text;
      }
      if (!c.commentingReply) {
        c.replying = false;
        c.draftReply = '';
      }
      c.confirmDelete = false;
    });
  }

  private countComments(card: WallSocialCard): number {
    return this.flattenComments(card).length;
  }

  private refreshRecentCommenters(card: WallSocialCard): void {
    const all = this.flattenComments(card)
      .slice()
      .sort((a, b) => {
        const ta = new Date(a.created as string).getTime();
        const tb = new Date(b.created as string).getTime();
        return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
      });
    const names: string[] = [];
    const seen = new Set<string>();
    all.forEach((c) => {
      const id = String(c.userID || '');
      const name = String(c.authorName || '').trim();
      const key = id || name;
      if (!key || seen.has(key)) {
        return;
      }
      seen.add(key);
      if (name && names.length < 3) {
        names.push(name);
      }
    });
    card.recentCommenters = names;
    card.commenterCount = seen.size;
  }

  private removeCommentFromCard(card: WallSocialCard, commentId: string): void {
    if ((card.comments || []).some((c) => c._id === commentId)) {
      card.comments = card.comments.filter((c) => c._id !== commentId);
      return;
    }
    card.comments.forEach((c) => {
      if (c.replies?.some((r) => r._id === commentId)) {
        c.replies = c.replies.filter((r) => r._id !== commentId);
      }
    });
  }

  private focusReplyField(parentId: string): void {
    const el = document.querySelector(
      `[data-reply-edit="${parentId}"]`
    ) as HTMLTextAreaElement | null;
    if (el) {
      this.fitCommentTextarea(el);
      el.focus();
    }
  }

  private showSnack(key: string): void {
    this.translate.get(key).subscribe((msg) => {
      this.snackBar.open(msg, '', { duration: 3500 });
    });
  }
}
